// Circuit breaker configuration with environment-based settings
import { resolveApiBaseUrl } from "@/config";

export interface CircuitBreakerSettings {
  threshold: number;
  timeout: number; // window timeout (legacy parameter)
  resetTimeout: number;
  healthCheckUrl?: string;
  enabled: boolean;
}

export interface CircuitBreakerConfiguration {
  default: CircuitBreakerSettings;
  studies: CircuitBreakerSettings;
  health: CircuitBreakerSettings;
  ai: CircuitBreakerSettings;
}

// Default configuration
const DEFAULT_CONFIG: CircuitBreakerConfiguration = {
  default: {
    threshold: 5,
    timeout: 60000,
    resetTimeout: 30000,
    enabled: true,
  },
  studies: {
    threshold: 5,
    timeout: 30000,
    resetTimeout: 15000,
    enabled: true,
  },
  health: {
    threshold: 10,
    timeout: 120000,
    resetTimeout: 60000,
    enabled: true,
  },
  ai: {
    threshold: 3,
    timeout: 60000,
    resetTimeout: 45000,
    enabled: true,
  },
};

// Development overrides (more lenient)
const DEVELOPMENT_CONFIG: Partial<CircuitBreakerConfiguration> = {
  default: {
    threshold: 10, // Higher threshold for development
    timeout: 60000,
    resetTimeout: 10000, // Faster reset in development
    enabled: true,
  },
  studies: {
    threshold: 5,
    timeout: 30000,
    resetTimeout: 5000,
    enabled: true,
  },
  health: {
    threshold: 15,
    timeout: 120000,
    resetTimeout: 30000,
    enabled: true,
  },
  ai: {
    threshold: 5,
    timeout: 60000,
    resetTimeout: 15000,
    enabled: true,
  },
};

// Production configuration (more strict)
const PRODUCTION_CONFIG: Partial<CircuitBreakerConfiguration> = {
  default: {
    threshold: 3, // Lower threshold for production
    timeout: 60000,
    resetTimeout: 60000, // Longer reset in production
    enabled: true,
  },
  studies: {
    threshold: 4,
    timeout: 30000,
    resetTimeout: 30000,
    enabled: true,
  },
  health: {
    threshold: 15, // Increase threshold to be more lenient in production
    timeout: 120000,
    resetTimeout: 60000, // Reduce reset timeout to 1 minute instead of 2
    enabled: true,
  },
  ai: {
    threshold: 2,
    timeout: 60000,
    resetTimeout: 90000,
    enabled: true,
  },
};

// Test environment configuration (disabled or very lenient)
const TEST_CONFIG: Partial<CircuitBreakerConfiguration> = {
  default: {
    threshold: 100,
    timeout: 60000,
    resetTimeout: 1000,
    enabled: false, // Disable in tests by default
  },
  studies: {
    threshold: 100,
    timeout: 30000,
    resetTimeout: 1000,
    enabled: false,
  },
  health: {
    threshold: 100,
    timeout: 120000,
    resetTimeout: 1000,
    enabled: false,
  },
  ai: {
    threshold: 100,
    timeout: 60000,
    resetTimeout: 1000,
    enabled: false,
  },
};

class CircuitBreakerConfigManager {
  private config: CircuitBreakerConfiguration;
  private environment: string;

  constructor() {
    this.environment = this.detectEnvironment();
    this.config = this.buildConfiguration();
  }

  private detectEnvironment(): string {
    // Check various environment indicators
    try {
      if (import.meta?.env) {
        const mode = import.meta.env.MODE;
        if (mode) return mode;
      }
    } catch (e) {
      // import.meta not available in this environment
    }

    if (typeof process !== "undefined" && process.env) {
      if (process.env.NODE_ENV) return process.env.NODE_ENV;
      if (process.env.VITE_APP_ENV) return process.env.VITE_APP_ENV;
    }

    // Check URL patterns
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
        return "development";
      }
      if (hostname.includes("staging") || hostname.includes("dev.")) {
        return "development";
      }
      if (hostname.includes("test")) {
        return "test";
      }
    }

    return "production";
  }

  private buildConfiguration(): CircuitBreakerConfiguration {
    let envConfig: Partial<CircuitBreakerConfiguration> = {};

    switch (this.environment) {
      case "development":
      case "dev":
        envConfig = DEVELOPMENT_CONFIG;
        break;
      case "test":
      case "testing":
        envConfig = TEST_CONFIG;
        break;
      case "production":
      case "prod":
        envConfig = PRODUCTION_CONFIG;
        break;
      default:
        envConfig = {};
    }

    // Deep merge configurations
    const config = { ...DEFAULT_CONFIG };

    Object.keys(config).forEach((key) => {
      const circuitKey = key as keyof CircuitBreakerConfiguration;
      if (envConfig[circuitKey]) {
        config[circuitKey] = {
          ...config[circuitKey],
          ...envConfig[circuitKey],
        };
      }
    });

    // Apply environment variable overrides
    this.applyEnvironmentVariables(config);

    // Add health check URLs based on API base URL
    this.addHealthCheckUrls(config);

    return config;
  }

  private applyEnvironmentVariables(config: CircuitBreakerConfiguration) {
    const getEnvVar = (name: string): string | undefined => {
      try {
        if (import.meta?.env) {
          return import.meta.env[name];
        }
      } catch {
        // import.meta not available
      }
      if (typeof process !== "undefined" && process.env) {
        return process.env[name];
      }
      return undefined;
    };

    // Global overrides
    const globalThreshold = getEnvVar("VITE_CIRCUIT_BREAKER_THRESHOLD");
    const globalResetTimeout = getEnvVar("VITE_CIRCUIT_BREAKER_RESET_TIMEOUT");
    const globalEnabled = getEnvVar("VITE_CIRCUIT_BREAKER_ENABLED");

    if (globalThreshold) {
      const threshold = parseInt(globalThreshold, 10);
      if (!isNaN(threshold)) {
        Object.keys(config).forEach((key) => {
          config[key as keyof CircuitBreakerConfiguration].threshold =
            threshold;
        });
      }
    }

    if (globalResetTimeout) {
      const resetTimeout = parseInt(globalResetTimeout, 10);
      if (!isNaN(resetTimeout)) {
        Object.keys(config).forEach((key) => {
          config[key as keyof CircuitBreakerConfiguration].resetTimeout =
            resetTimeout;
        });
      }
    }

    if (globalEnabled) {
      const enabled = globalEnabled.toLowerCase() === "true";
      Object.keys(config).forEach((key) => {
        config[key as keyof CircuitBreakerConfiguration].enabled = enabled;
      });
    }

    // Specific circuit breaker overrides
    const circuits = ["default", "studies", "health", "ai"] as const;

    circuits.forEach((circuit) => {
      const prefix = `VITE_CIRCUIT_BREAKER_${circuit.toUpperCase()}`;

      const threshold = getEnvVar(`${prefix}_THRESHOLD`);
      if (threshold && !isNaN(parseInt(threshold, 10))) {
        config[circuit].threshold = parseInt(threshold, 10);
      }

      const resetTimeout = getEnvVar(`${prefix}_RESET_TIMEOUT`);
      if (resetTimeout && !isNaN(parseInt(resetTimeout, 10))) {
        config[circuit].resetTimeout = parseInt(resetTimeout, 10);
      }

      const enabled = getEnvVar(`${prefix}_ENABLED`);
      if (enabled) {
        config[circuit].enabled = enabled.toLowerCase() === "true";
      }

      const healthUrl = getEnvVar(`${prefix}_HEALTH_URL`);
      if (healthUrl) {
        config[circuit].healthCheckUrl = healthUrl;
      }
    });
  }

  private addHealthCheckUrls(config: CircuitBreakerConfiguration) {
    const baseUrl = resolveApiBaseUrl();
    const buildUrl = (path: string) => {
      if (!baseUrl) return path;
      if (baseUrl.startsWith("http")) {
        return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
      }
      // Relative path (e.g., "/api")
      const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
      return `${normalizedBase}${path.startsWith("/") ? path : `/${path}`}`;
    };

    // Set default health check URLs if not already configured
    if (!config.default.healthCheckUrl) {
      config.default.healthCheckUrl = buildUrl("/health");
    }

    if (!config.studies.healthCheckUrl) {
      config.studies.healthCheckUrl = buildUrl("/studies?limit=1");
    }

    if (!config.health.healthCheckUrl) {
      config.health.healthCheckUrl = buildUrl("/health");
    }

    if (!config.ai.healthCheckUrl) {
      config.ai.healthCheckUrl = buildUrl("/ai-insights/health");
    }
  }

  // Public API
  getConfiguration(): CircuitBreakerConfiguration {
    return { ...this.config };
  }

  getCircuitConfig(
    name: keyof CircuitBreakerConfiguration,
  ): CircuitBreakerSettings {
    return { ...this.config[name] };
  }

  getEnvironment(): string {
    return this.environment;
  }

  isEnabled(name: keyof CircuitBreakerConfiguration): boolean {
    return this.config[name].enabled;
  }

  // Update configuration at runtime (for testing or dynamic configuration)
  updateConfig(updates: Partial<CircuitBreakerConfiguration>) {
    Object.keys(updates).forEach((key) => {
      const circuitKey = key as keyof CircuitBreakerConfiguration;
      if (updates[circuitKey]) {
        this.config[circuitKey] = {
          ...this.config[circuitKey],
          ...updates[circuitKey],
        };
      }
    });
  }

  // Get configuration summary for debugging
  getConfigSummary() {
    return {
      environment: this.environment,
      circuitBreakers: Object.keys(this.config).reduce(
        (summary, key) => {
          const circuitKey = key as keyof CircuitBreakerConfiguration;
          const config = this.config[circuitKey];
          summary[circuitKey] = {
            enabled: config.enabled,
            threshold: config.threshold,
            resetTimeout: config.resetTimeout,
            hasHealthCheck: !!config.healthCheckUrl,
          };
          return summary;
        },
        {} as Record<string, any>,
      ),
    };
  }
}

// Global configuration manager
export const circuitBreakerConfig = new CircuitBreakerConfigManager();

// Export for easier imports
export const getCircuitBreakerConfig = () =>
  circuitBreakerConfig.getConfiguration();
export const getCircuitConfig = (name: keyof CircuitBreakerConfiguration) =>
  circuitBreakerConfig.getCircuitConfig(name);
export const isCircuitBreakerEnabled = (
  name: keyof CircuitBreakerConfiguration,
) => circuitBreakerConfig.isEnabled(name);
